import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './Logs.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface LogEntry {
  id: string;
  eventId: string;
  userId?: string; // Only present in admin action logs
  action?: string; // Only present in admin action logs
  details?: any;
  metadata?: any; // Some logs use metadata instead of details
  timestamp: string;
  // Event email log properties (stored directly on log object)
  type?: string;
  status?: string;
  subject?: string;
  recipientEmail?: string;
  recipientName?: string;
  recipientUserId?: string;
  partnerName?: string;
  eventName?: string;
  eventDate?: string;
  errorMessage?: string;
  emailBodyHtml?: string;
  emailBodyPlainText?: string;
  hostNames?: string;
  hostAddress?: string;
  courseTime?: string;
  course?: string;
  guestCount?: number;
  dietaryRestrictions?: string[];
  messageSubject?: string;
}

const Logs: React.FC = () => {
  const { accessToken } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<'admin' | 'events'>('admin');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => {
    fetchLogs();
  }, [accessToken]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/logs`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setLogs(response.data.logs || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente logger');
    } finally {
      setLoading(false);
    }
  };

  // Filter logs based on active sub-tab
  const filteredByTab = logs.filter(log => {
    if (activeSubTab === 'admin') {
      // Admin actions: either eventId is 'admin-action' OR log has an 'action' field (system logs)
      return log.eventId === 'admin-action' || (log.action && !log.type);
    } else {
      // Event logs: has eventId (not admin-action) and either has 'type' or no 'action'
      return log.eventId !== 'admin-action' && (log.type || !log.action);
    }
  });

  // Get unique actions for admin actions
  const uniqueActions = Array.from(new Set(
    logs.filter(log => log.eventId === 'admin-action' && log.action).map(log => log.action!)
  )).sort();

  // Apply search and action filter
  const filteredLogs = filteredByTab
    .filter(log => {
      const matchesSearch = 
        (log.action?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (log.userId?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (log.type?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (log.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (log.recipientName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        JSON.stringify(log.details || log.metadata || {}).toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesAction = activeSubTab !== 'admin' || actionFilter === 'all' || log.action === actionFilter;
      
      return matchesSearch && matchesAction;
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action?: string) => {
    if (!action) return '-';
    return action.replace(/_/g, ' ');
  };

  const formatDetails = (details: any) => {
    if (!details) return '-';
    
    // Create a readable summary based on common patterns
    const parts: string[] = [];
    
    if (details.eventName) parts.push(`Event: ${details.eventName}`);
    if (details.eventId) parts.push(`ID: ${details.eventId}`);
    if (details.billingId) parts.push(`Billing: ${details.billingId}`);
    if (details.userId) parts.push(`User: ${details.userId}`);
    if (details.changes) {
      const changes = Object.entries(details.changes)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]: [string, any]) => {
          if (value.from !== undefined && value.to !== undefined) {
            return `${key}: ${value.from} → ${value.to}`;
          }
          return `${key}: ${JSON.stringify(value)}`;
        });
      parts.push(...changes);
    }
    if (details.amount !== undefined) parts.push(`Amount: ${details.amount} kr`);
    if (details.status) parts.push(`Status: ${details.status}`);
    
    return parts.length > 0 ? parts.join(' | ') : JSON.stringify(details);
  };

  const getEmailTypeLabel = (type?: string) => {
    if (!type) return 'Ukjent';
    const typeLabels: Record<string, string> = {
      host_notification: '🍽️ Vertsvarsling',
      registration_confirmation: '✅ Påmeldingsbekreftelse',
      organizer_message: '💬 Arrangørmelding',
      event_cancellation: '❌ Avlysning'
    };
    return typeLabels[type] || type;
  };

  const getCourseLabel = (course?: string) => {
    if (!course) return '';
    const courseLabels: Record<string, string> = {
      appetizer: '🥗 Forrett',
      mainCourse: '🍽️ Hovedrett',
      dessert: '🍰 Dessert'
    };
    return courseLabels[course] || course;
  };

  const getCourseColor = (course?: string) => {
    if (!course) return '#E5E7EB';
    const colors: Record<string, string> = {
      appetizer: '#DBEAFE',
      mainCourse: '#FEF3C7',
      dessert: '#FCE7F3'
    };
    return colors[course] || '#E5E7EB';
  };

  if (loading) {
    return <div className="loading-state">Laster logger...</div>;
  }

  return (
    <div className="logs-management">
      <div className="management-header">
        <h2>Logg</h2>
        <p className="logs-subtitle">Systemlogger og hendelser</p>
      </div>

      {error && <div className="message-banner error">{error}</div>}

      {/* Sub-tabs */}
      <div className="sub-tabs">
        <button
          className={`sub-tab ${activeSubTab === 'admin' ? 'active' : ''}`}
          onClick={() => {
            setActiveSubTab('admin');
            setActionFilter('all');
          }}
        >
          Admin Actions
        </button>
        <button
          className={`sub-tab ${activeSubTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('events')}
        >
          Sykkelfester
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Søk i logger..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {activeSubTab === 'admin' && (
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="action-filter"
          >
            <option value="all">Alle handlinger</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>
                {formatAction(action)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Logs Table */}
      <div className="logs-table-container">
        {activeSubTab === 'admin' ? (
          <table className="logs-table">
            <thead>
              <tr>
                <th>Tidspunkt</th>
                <th>Handling</th>
                <th>Bruker ID</th>
                <th>Detaljer</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-state">
                    Ingen logger funnet
                  </td>
                </tr>
              ) : (
                filteredLogs.map(log => (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td className="timestamp-cell">{formatTimestamp(log.timestamp)}</td>
                      <td className="action-cell">{formatAction(log.action)}</td>
                      <td className="user-id-cell">{log.userId}</td>
                      <td className="details-cell">
                        <button
                          onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                          className="details-expand-button"
                        >
                          {expandedLogId === log.id ? '▼' : '▶'} Se detaljer
                        </button>
                      </td>
                    </tr>
                    {expandedLogId === log.id && (
                      <tr className="details-row">
                        <td colSpan={4}>
                          <div className="details-content">
                            <pre>{JSON.stringify(log.details || log.metadata || {}, null, 2)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <div className="logs-cards">
            {filteredLogs.length === 0 ? (
              <p className="empty-state">Ingen logger funnet</p>
            ) : (
              filteredLogs.map((log) => {
                // Check if this is an email log or system log
                const isEmailLog = log.type || log.subject;
                const isSystemLog = log.action && !log.type;

                if (isEmailLog) {
                  // Email log display
                  const typeLabel = getEmailTypeLabel(log.type || log.action);
                  const isUnverified = log.status === 'skipped' && log.errorMessage === 'Email not verified';

                  return (
                    <div
                      key={log.id}
                      className={`log-card ${log.status === 'success' ? 'success' : isUnverified ? 'warning' : 'error'}`}
                    >
                      <div className="log-card-header">
                        <div>
                          <strong className="log-status">
                            {log.status === 'success' ? '✅ Sendt' : isUnverified ? '⚠️ Ikke Sendt - Epostadresse ikke verifisert' : '❌ Feilet'}
                          </strong>
                          <p className="log-timestamp">
                            {formatTimestamp(log.timestamp)}
                          </p>
                        </div>
                        <div className="log-badges">
                          <span className="log-type-badge">{typeLabel}</span>
                          {log.course && (
                            <span 
                              className="log-course-badge"
                              style={{ backgroundColor: getCourseColor(log.course) }}
                            >
                              {getCourseLabel(log.course)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="log-card-body">
                        <div className="log-field">
                          <strong>Event ID:</strong> {log.eventId}
                        </div>

                        {log.subject && (
                          <div className="log-field">
                            <strong>Emne:</strong> {log.subject}
                          </div>
                        )}

                        {log.hostNames && (
                          <div className="log-field">
                            <strong>Verter:</strong> {log.hostNames}
                          </div>
                        )}

                        {log.hostAddress && (
                          <div className="log-field">
                            <strong>Adresse:</strong> {log.hostAddress}
                          </div>
                        )}

                        {log.courseTime && (
                          <div className="log-field">
                            <strong>Tidspunkt:</strong> {log.courseTime}
                          </div>
                        )}

                        {log.recipientName && (
                          <div className="log-field">
                            <strong>Mottaker:</strong>
                            <div className="log-recipient">
                              • {log.recipientName} ({log.recipientEmail})
                              {log.recipientUserId && (
                                <span className="recipient-user-id">
                                  ID: {log.recipientUserId}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {log.partnerName && (
                          <div className="log-field log-partner">
                            Partner: {log.partnerName}
                          </div>
                        )}

                        {log.guestCount !== undefined && (
                          <div className="log-field">
                            <strong>Antall gjester:</strong> {log.guestCount} par ({log.guestCount * 2} personer)
                          </div>
                        )}

                        {log.dietaryRestrictions && log.dietaryRestrictions.length > 0 && (
                          <div className="log-field">
                            <strong>Diettbehov:</strong> {log.dietaryRestrictions.join(', ')}
                          </div>
                        )}

                        {log.messageSubject && (
                          <div className="log-field">
                            <strong>Meldingsemne:</strong> {log.messageSubject}
                          </div>
                        )}

                        {log.errorMessage && (
                          <div className="log-error-message">
                            <strong>Feilmelding:</strong> {log.errorMessage}
                          </div>
                        )}

                        {log.emailBodyPlainText && (
                          <div className="log-email-content">
                            <button
                              onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                              className="expand-button"
                            >
                              {expandedLogId === log.id ? '▼' : '▶'} Se e-postens innhold
                            </button>
                            {expandedLogId === log.id && (
                              <div className="email-body">
                                {log.emailBodyPlainText}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } else if (isSystemLog) {
                  // System log display
                  const actionLabels: Record<string, string> = {
                    REGISTRATION_CLOSED: '🔒 Påmelding stengt',
                    ROUTES_GENERATED: '🗺️ Ruter generert',
                    EVENT_CONFIRMED: '✅ Arrangement bekreftet',
                    EVENT_ACTIVATED: '🎉 Arrangement aktivert',
                    EVENT_COMPLETED: '🏁 Arrangement fullført'
                  };
                  const actionLabel = log.action ? (actionLabels[log.action] || log.action) : 'Ukjent';
                  const actionColors: Record<string, string> = {
                    REGISTRATION_CLOSED: '#F59E0B',
                    ROUTES_GENERATED: '#3B82F6',
                    EVENT_CONFIRMED: '#10B981',
                    EVENT_ACTIVATED: '#8B5CF6',
                    EVENT_COMPLETED: '#6B7280'
                  };
                  const actionColor = log.action ? (actionColors[log.action] || '#6B7280') : '#6B7280';

                  return (
                    <div
                      key={log.id}
                      className="log-card system"
                      style={{ borderLeft: `4px solid ${actionColor}` }}
                    >
                      <div className="log-card-header">
                        <div>
                          <strong className="log-status" style={{ color: actionColor }}>
                            {actionLabel}
                          </strong>
                          <p className="log-timestamp">
                            {formatTimestamp(log.timestamp)}
                          </p>
                        </div>
                      </div>

                      <div className="log-card-body">
                        <div className="log-field">
                          <strong>Event ID:</strong> {log.eventId}
                        </div>

                        {log.details && (
                          <div className="log-field">
                            <button
                              onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                              className="expand-button"
                            >
                              {expandedLogId === log.id ? '▼' : '▶'} Se detaljer
                            </button>
                            {expandedLogId === log.id && (
                              <div className="email-body" style={{ marginTop: '12px' }}>
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px' }}>
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  // Fallback for unknown log type
                  return (
                    <div key={log.id} className="log-card">
                      <div className="log-card-body">
                        <div className="log-field">
                          <strong>Ukjent loggtype</strong>
                        </div>
                        <pre style={{ fontSize: '12px', marginTop: '8px' }}>
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </div>
                    </div>
                  );
                }
              })
            )}
          </div>
        )}
      </div>

      <div className="logs-summary">
        Viser {filteredLogs.length} av {filteredByTab.length} logger
      </div>
    </div>
  );
};

export default Logs;
